<template>
  <div class="station-card" :class="{ 'dark': darkMode, [status]: [status] }">
    <RouterLink class="mobileclick"
      tag="div"
      :to="{ name: 'mobile-station-calendar', params: { stationId: station.id } }"></RouterLink>

    <div class="titles">
      <input class="checkbox"
        type="checkbox"
        name="station-title"
        :id="station.id"
        @change="emitCheckedStation($event)"
        :checked="checked" />
      <label class="f5"
        :for="station.id">{{ station.title }}</label>
      <div v-if="!isNetworkAvailable"
        class="safetymodeon">
        <UIIcon name="alert-triangle" />
        <span class="f8">{{ $t('Safety Mode') }}</span>
      </div>
      <div v-if="isNetworkAvailable && !station.safetyMode"
        class="settings"
        @click="openStationSettings(station.id)">
        <UIIcon name="settings" />
      </div>
    </div>

    <div class="undertitle">
      <div class="left">
        <img v-if="image && isNetworkAvailable"
          :src="image"
          :alt="(station.playing) ? station.playing.title : station.title">
        <div v-if="!image && isNetworkAvailable"
          class="noton">
          <UIIcon name="circle-crossed" />
        </div>
        <div v-if="!isNetworkAvailable"
          class="not-on">
          <UIIcon name="cloud-unavailable" />
        </div>
      </div>
      <div class="right">
        <span v-if="station.ageLimit > 0"
          class="age f6">{{ $t('Age Limit:') }} {{ station.ageLimit }}</span>
        <span v-else
          class="age f6">{{ $t('No Age Limit') }}</span>
        <span v-if="station.operatorTagsRequireAll"
          class="age f6">{{ $t('operator tags type: all') }}</span>
        <span v-else
          class="age f6">{{ $t('operator tags type: one of these tags') }}</span>
        <span v-if="station.operatorTags"
          class="age f6"> {{ $t('operatortags:') }}
          {{ station.operatorTags.length }}</span>
        <Batteries v-if="isNetworkAvailable"
          :leftController="station.leftController"
          :rightController="station.rightController" />
      </div>
    </div>

    <div v-if="title && isNetworkAvailable"
      class="gametitle">
      <h2 class="f8">{{ title }}</h2>
    </div>

    <UIIcon class="maintenance"
      :disabled="!isNetworkAvailable"
      v-if="station.maintenanceModeEnabled"
      :title="$t('Maintenance Mode Enabled')"
      @click="openStationSettings(station.id)"
      name="alert-triangle" />

    <UseVuex v-slot="{ state }">
      <StationCardTimer v-if="station._endTime"
        :currentTime="state.timer.currentTime"
        :station="station"
        :twentyFourHourTime="twentyFourHourTime"
        @timerStatusChange="setStatus" />
    </UseVuex>

    <UIButton :tiny="true"
      icon="rotate-cw"
      class="restorebutton"
      @click="restoreStation"
      v-if="isNetworkAvailable && station.safetyMode">{{ $t('Restore Station') }}</UIButton>
    <UIButton :ghost="true"
      class="walkinbutton"
      @click="openWalkin"
      v-if="isNetworkAvailable && status === '-available'">{{ $t('Walk-In') }}</UIButton>
    <span class="message f6 ended"
      v-if="status === 'ended'">{{ $t('Time Ended') }}</span>
    <StationCalling :calling="station.calling"
      @clearCalling="$emit('stationClearCalling', station.id)" />
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import Batteries from './Batteries';
import StationCardTimer from './StationCardTimer';
import StationCalling from './StationCalling';
import UIButton from '@/core/shared/components/ui/UIButton';
import UIIcon from '@/core/shared/components/ui/UIIcon';
import UICheckbox from '@/core/shared/components/ui/UICheckbox';
import UseVuex from '@/core/shared/components/utils/UseVuex';

@Component({
  components: {
    Batteries,
    StationCardTimer,
    StationCalling,
    UIButton,
    UIIcon,
    UICheckbox,
    UseVuex,
  },
  props: {
    station: {
      type: Object,
      required: true,
    },
    checked: {
      type: Boolean,
      default: false,
    },
    isNetworkAvailable: {
      type: Boolean,
      required: true,
    },
    darkMode: {
      type: Boolean,
      required: false,
    },
  },
  inheritAttrs: false,
})
export default class StationCard extends Vue {
  statusFromTimer = null;
  endedTimeout = null;
  filteredTags = [];

  get image () {
    if (this.station.powerStatus === 1 && this.station.playing && this.station.playing.imageUrl) {
      return this.station.playing.imageUrl;
    } else if (this.station.powerStatus === 1) {
      return '/images/launcher.png';
    }
  }

  get twentyFourHourTime () {
    return this.$store.getters['entities/Location'](this.station.location.id).twentyFourHourTime;
  }

  get title () {
    if (this.station.playing) {
      return this.station.playing.title;
    } else if (this.station.powerStatus === 1) {
      return this.$t('Launcher');
    }
  }

  get upgradeUrl () {
    return '//' + process.env.PANEL_URL + '/manage#/stations';
  }

  get status () {
    if (!this.isNetworkAvailable && (!this.station._endTime || this.statusFromTimer === 'paused')) {
      return '-warning';
    } else if (this.station._endTime) {
      return this.statusFromTimer;
    } else if (this.statusFromTimer === 'ended') {
      return '-ended';
    } else {
      return '-available';
    }
  }

  setStatus (status) {
    clearTimeout(this.endedTimeout);
    this.statusFromTimer = status;

    if (status === 'ended') {
      this.endedTimeout = setTimeout(() => {
        this.statusFromTimer = null;
      }, 30000);
    }
  }

  openWalkin () {
    this.$router.push({
      name: 'walkin',
      params: {
        preSelected: {
          _startNow: true,
          bookingStationTimes: [
            {
              station: {
                id: this.station.id,
              },
              experience: {
                id: this.station.experience.id,
              },
            },
          ],
        },
        darkMode: this.darkMode,
      },
    });
  }

  openStationSettings (stationId) {
    const darkMode = this.darkMode;
    this.$store.commit('setOverviewScrollPosition');
    this.$router.push({ name: 'station-settings', params: { stationId, darkMode } });
  }

  emitCheckedStation () {
    this.$emit('checkedStation', this.station.id);
  }

  restoreStation () {
    this.$emit('restoreStation', this.station.id);
  }
}
</script>

<style scoped lang="postcss">
@import "core/shared/styles";

.station-card {
  position: relative;
  background-color: var(--colorWhite);
  padding: var(--spacingXS) var(--spacingSm);
  height: 13rem;
  border-top: 0.1rem solid var(--colorCasper);
  overflow: hidden;
  box-shadow: var(--spacingMd);

  & .mobileclick {
    position: absolute;
    top: 0;
    left: 0;
    width: 75%;
    height: 100%;
    cursor: pointer;
    z-index: 1;

    @media (--tablet) {
      display: none;
    }
  }

  &:last-child {
    border-bottom: 0.1rem solid var(--colorCasper);
  }

  & .titles {
    position: relative;

    & .checkbox {
      margin: 0 var(--spacingXS) var(--spacingXS) 0;

      display: none;

      @media (--tablet) {
        display: inline-block;
      }
    }

    & .safetymodeon {
      display: flex;
      align-items: center;
      margin-bottom: var(--spacingXS);

      & svg {
        fill: var(--colorErrorFlat);
        width: 1.5rem;
        height: 1.5rem;
        margin-right: var(--spacingXS);
      }

      & span {
        color: var(--colorErrorFlat);
      }
    }

    & .settings {
      position: absolute;
      cursor: pointer;
      top: 0;
      right: 0;

      & svg {
        fill: var(--colorGeyser);
        width: 1.5rem;
        height: 1.5rem;
      }
    }
  }

  & .undertitle {
    display: flex;

    & .left {
      & img {
        width: 10rem;
        height: 4.5rem;
      }

      & .noton {
        border: 0.1rem solid var(--colorGeyser);
        width: 10rem;
        height: 4.7rem;
        display: flex;
        align-items: center;
        justify-content: center;

        & svg {
          width: 2rem;
          height: 2rem;
          fill: var(--colorGeyser);
        }
      }
    }

    & .right {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      margin-left: var(--spacingSm);

      & .age {
        margin: 0 0 var(--spacingXS);
        color: var(--colorManatee);
      }
    }
  }

  & .gametitle {
    & h2 {
      font-size: 1rem;
      margin-top: 0.2rem;
    }
  }

  & .station-card-timer {
    position: absolute;
    text-align: right;
    bottom: var(--spacingSm);
    right: var(--spacingSm);
  }

  /* Status styles */
  &.-available {
    border-right: 0.5rem solid var(--colorInfoFlat);
  }

  &.-warning {
    border-right: 0.5rem solid var(--colorErrorFlat);
  }

  &.-error,
  &.-ended {
    border-right: 0.5rem solid var(--colorSBRedFlat);
  }

  & .message {
    margin: 0;
    font-size: 1rem;
    position: absolute;
    bottom: var(--spacingSm);
    right: var(--spacingSm);
    text-align: right;

    &.available {
      color: var(--colorInfoFlat);
    }

    &.ended {
      color: var(--colorSBRedFlat);
    }
  }

  & .restorebutton {
    position: absolute;
    top: var(--spacingXS);
    right: var(--spacingSm);
  }

  & .walkinbutton {
    position: absolute;
    bottom: var(--spacingSm);
    right: var(--spacingSm);
    padding: var(--spacingXS) var(--spacingSm);
    color: var(--colorInfoFlat);
    border-color: var(--colorInfoFlat);

    &:hover {
      color: var(--colorInfoFlatHover);
      border-color: var(--colorInfoFlatHover);
    }
  }

  & .maintenance {
    position: absolute;
    right: var(--spacingSm);
    top: 5rem;
    width: 2rem;
    height: 2rem;
    fill: var(--colorErrorFlat);
    cursor: pointer;

    &.disabled {
      pointer-events: none;
      cursor: not-allowed;
    }
  }

  /* For being above the mobile click when on mobile */
  & .calling-container {
    z-index: 2;
  }
}

.station-card.dark {
  background-color: var(--darkModeColorDarkBackground);
  color: var(--darkModeColorText) !important;
  border-top: solid var(--darkModeColorLightBackground) 1px;
}

.station-card.dark .walkinbutton {
  background-color: var(--darkModeColorDarkBackground);
}
</style>
